import { createContext, useContext, useMemo } from "react";

import { useGetTaggableSameCompanyUsers } from "@/hooks/queries/useGetTaggableSameCompanyUsers";
import { storage } from "@/lib/storage";
import { User, UserRole } from "@/types";

interface UserOption {
  value: number;
  label: string;
}

interface TaggableUsersContextValue {
  allCurrentEmployees: User[];
  salesOptions: UserOption[];
  users: User[];
  userOptions: UserOption[];
  userFirstNameOptions: UserOption[];
  userInitialsMap: Record<number | string, string>;
  userNameMap: Record<number | string, string>;
  userFirstNameMap: Record<number | string, string>;
}

const TaggableUsersContext = createContext<
  TaggableUsersContextValue | undefined
>(undefined);

const sortByFullName = (a: User, b: User) => {
  const aFullName = `${a.firstName} ${a.lastName}`;
  const bFullName = `${b.firstName} ${b.lastName}`;
  return aFullName.localeCompare(bFullName);
};

export const TaggableUsersProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { data: users } = useGetTaggableSameCompanyUsers();

  const value = useMemo(() => {
    const result: TaggableUsersContextValue = {
      allCurrentEmployees: [],
      salesOptions: [],
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      users: users || [],
      userOptions: [],
      userFirstNameOptions: [],
      userInitialsMap: { unassigned: "N/A" },
      userNameMap: { unassigned: "Unassigned" },
      userFirstNameMap: { unassigned: "Unassigned" },
    };

    if (!users) return result;

    result.allCurrentEmployees = users.filter(
      (u) => u.deletedAt === null && u.userRole,
    );

    const usersWhoAreStaffers = users.filter(
      (u) => u.quoPhoneNumberId !== null,
    );

    const currentUser = storage.getUser();

    // Sort active users - show current user, then staffers, then remaining users
    const currentUserInList = usersWhoAreStaffers.find(
      (user) => user.id === currentUser?.id && user.deletedAt === null,
    );
    const staffers = usersWhoAreStaffers
      .filter(
        (user) =>
          user.deletedAt === null &&
          user.userRole === UserRole.STAFFER &&
          user.id !== currentUser?.id,
      )
      .sort(sortByFullName);
    const remainingActive = usersWhoAreStaffers
      .filter(
        (user) =>
          user.deletedAt === null &&
          user.userRole !== UserRole.STAFFER &&
          user.id !== currentUser?.id,
      )
      .sort(sortByFullName);
    const inactiveUsers = usersWhoAreStaffers.filter(
      (user) => user.deletedAt !== null,
    );

    const sortedActive = [
      ...(currentUserInList ? [currentUserInList] : []),
      ...staffers,
      ...remainingActive,
    ];

    result.users = [...sortedActive, ...inactiveUsers];

    sortedActive.forEach((user) => {
      const fullName = `${user.firstName} ${user.lastName}`;
      result.userOptions.push({ value: user.id, label: fullName });
      result.userFirstNameOptions.push({
        value: user.id,
        label: user.firstName,
      });
      result.userInitialsMap[user.id] =
        `${user.firstName[0]}${user.lastName[0]}`;
      result.userNameMap[user.id] = fullName;

      result.userFirstNameMap[user.id] = user.firstName;
    });

    inactiveUsers.forEach((user) => {
      const fullName = `${user.firstName} ${user.lastName}`;
      result.userInitialsMap[user.id] =
        `${user.firstName[0]}${user.lastName[0]}`;
      result.userNameMap[user.id] = fullName;
    });

    // Name/initials lookups must resolve *any* same-company user — the loops
    // above only cover staffers (quoPhoneNumberId !== null), so roles like SALES
    // would otherwise be missing. Fill in anyone not already mapped.
    users.forEach((user) => {
      if (user.id in result.userNameMap) return;
      result.userNameMap[user.id] = `${user.firstName} ${user.lastName}`;
      result.userInitialsMap[user.id] =
        `${user.firstName[0]}${user.lastName[0]}`;
      result.userFirstNameMap[user.id] = user.firstName;
    });

    // Sales options come from all active SALES users (not just staffers).
    result.salesOptions = users
      .filter(
        (user) => user.userRole === UserRole.SALES && user.deletedAt === null,
      )
      .sort(sortByFullName)
      .map((user) => ({
        value: user.id,
        label: `${user.firstName} ${user.lastName}`,
      }));

    return result;
  }, [users]);

  return (
    <TaggableUsersContext.Provider value={value}>
      {children}
    </TaggableUsersContext.Provider>
  );
};

const EMPTY_CONTEXT: TaggableUsersContextValue = {
  allCurrentEmployees: [],
  salesOptions: [],
  users: [],
  userOptions: [],
  userFirstNameOptions: [],
  userInitialsMap: {},
  userNameMap: {},
  userFirstNameMap: {},
};

export const useTaggableUsers = () => {
  const context = useContext(TaggableUsersContext);

  // Support hot reload without crashing - Vite issue
  if (!context) {
    if (import.meta.env.DEV) return EMPTY_CONTEXT;

    throw new Error(
      "useTaggableUsers must be used within a TaggableUsersProvider",
    );
  }
  return context;
};

// Inactive users are hidden from dropdowns, but records assigned to them before
// deletion still need to show them (otherwise they can't be unassigned). These
// helpers merge any currently-selected inactive users back in with an
// "(inactive)" suffix.
export const mergeUserOptionsWithSelected = (
  userOptions: UserOption[],
  userNameMap: Record<number | string, string>,
  selectedIds: (number | null | undefined)[],
): UserOption[] => {
  const present = new Set(userOptions.map((o) => o.value));
  const inactiveSelected: UserOption[] = [];
  for (const id of selectedIds) {
    if (id == null) continue;
    if (present.has(id)) continue;
    const name = userNameMap[id];
    if (!name) continue;
    present.add(id);
    inactiveSelected.push({ value: id, label: `${name} (inactive)` });
  }
  return inactiveSelected.length === 0
    ? userOptions
    : [...userOptions, ...inactiveSelected];
};

// Hook used to get all users with their names
export const useUserOptionsWithSelected = (
  selectedIds: (number | null | undefined)[],
): UserOption[] => {
  const { userOptions, userNameMap } = useTaggableUsers();
  return useMemo(
    () => mergeUserOptionsWithSelected(userOptions, userNameMap, selectedIds),
    [userOptions, userNameMap, selectedIds],
  );
};

// Hook used to get sales users with their names
export const useSalesOptionsWithSelected = (
  selectedIds: (number | null | undefined)[],
): UserOption[] => {
  const { salesOptions, userNameMap } = useTaggableUsers();
  return useMemo(
    () => mergeUserOptionsWithSelected(salesOptions, userNameMap, selectedIds),
    [salesOptions, userNameMap, selectedIds],
  );
};

// Hook used to get the ids of deactivated (deleted) users, e.g. to flag
// former staffers who still show up on the leaderboard with past revenue.
export const useInactiveUserIds = (): Set<number> => {
  const { users } = useTaggableUsers();
  return useMemo(
    () => new Set(users.filter((u) => u.deletedAt !== null).map((u) => u.id)),
    [users],
  );
};
